var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useSWR, { useSWRConfig } from "swr";
import { axiosPrivate } from "../../api/axios";
import { AUTH_SWR_KEYS } from "../Utilities/swrKeys";
import { convertToCartVisibilitySettings } from "../../Shared/components/OrderSummary/orderSummary-utils";
/**
 * useCartSettingsQuery(sku) — fetch and cache cart display settings for a given SKU.
 *
 * Uses SWR for deduplication — multiple components requesting the same SKU
 * share a single API call and cached result.
 *
 * This is NOT hydrated (unlike delivery addresses, customer info, etc.) because
 * the SKU is not known at app load time — it depends on which orders or flows
 * the user is viewing. Instead, it fetches on first demand for a given SKU and
 * then serves from cache for subsequent consumers of the same SKU.
 *
 * @param sku - The plan SKU to fetch cart settings for
 * @param cartSettings - Pre-loaded settings; skips fetch when provided
 */
export const useCartSettingsQuery = (sku, cartSettings = undefined) => {
    var _a;
    const shouldFetch = !cartSettings;
    const key = shouldFetch ? `${AUTH_SWR_KEYS.CART_SETTINGS}-${sku}` : null;
    // Check if data already exists in the SWR cache for this key.
    // This lets us allow the initial fetch (revalidateOnMount: true) while
    // skipping re-fetches when data is already cached (revalidateOnMount: false).
    // Needed because the root SWRConfig sets revalidateOnMount: true globally,
    // which overrides revalidateIfStale: false.
    const { cache } = useSWRConfig();
    const hasCachedData = key ? ((_a = cache.get(key)) === null || _a === void 0 ? void 0 : _a.data) !== undefined : false;
    const { data, isLoading, error } = useSWR(key, () => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield axiosPrivate.get(`/Account/CartSettings?sku=${sku}`);
        if (data.success === false)
            throw new Error("Failed to load the SKU for your order.");
        const { CartSettings } = data;
        CartSettings.HidePrices = data.HidePrices;
        return convertToCartVisibilitySettings(CartSettings, CartSettings.HidePrices);
    }), {
        revalidateOnMount: !hasCachedData,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
    });
    return { settings: cartSettings !== null && cartSettings !== void 0 ? cartSettings : data, loading: isLoading, error: error === null || error === void 0 ? void 0 : error.message };
};
